import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarEvent as EngineEvent, HouseholdMember, Role, DataScope } from '@/types';
import { TimeIndicator } from './TimeIndicator';
import { TravelBuffer } from './TravelBuffer';
import { MapPin, Shield, User as UserIcon, Layers } from 'lucide-react';
import clsx from 'clsx';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  format,
  isBefore,
} from 'date-fns';
import { getEventPosition, getEventHeight, snapToInterval, pixelsToDateTime } from '../../../utils/calendarUtils';
import { clusterOverlappingEvents } from '../utils/calendarEngine';

interface CalendarGridProps {
  view: 'day' | 'week' | 'month';
  events: EngineEvent[];
  activeMembers: string[];
  isTravelEnabled: boolean;
  isGhostLayerEnabled: boolean;
  hourHeight: number;
  householdMembers: HouseholdMember[];
  currentUserId: string;
  currentHouseholdId: string | null;
  onEventClick: (event: EngineEvent) => void;
  activeScope: DataScope;
  currentDate: Date;
  onViewChange: (newView: 'day' | 'week' | 'month') => void;
  onDateChange: (newDate: Date) => void;
  onDragToCreate: (start: Date, end: Date, colIndex: number) => void;  
}


export const CalendarGrid: React.FC<CalendarGridProps> = ({
  view,
  events,
  activeMembers,
  isTravelEnabled,
  isGhostLayerEnabled,
  hourHeight = 80,
  householdMembers,
  currentUserId,
  currentHouseholdId,
  onEventClick,
  activeScope,
  currentDate,
  onViewChange,
  onDateChange,
  onDragToCreate,
}) => {
  const { t } = useTranslation();
  const calendarGridBodyRef = useRef<HTMLDivElement>(null);
  
  const [isDraggingForCreate, setIsDraggingForCreate] = useState(false);
  const [dragStartPixelY, setDragStartPixelY] = useState(0);
  const [dragCurrentPixelY, setDragCurrentPixelY] = useState(0);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(-1);

  const compactHourHeight = 60; // 1/4 reduction from 80px (original hourHeight)
  const allDayRowHeight = 60;   // Height for the all-day events row

  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Helper to check if an event is an all-day event for a specific date (heuristic)
  const isAllDayEventForDate = (event: EngineEvent, date: Date) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);

    // Consider an event "all-day" if it starts at 00:00, ends at 23:59, and spans a single day
    const startsAtMidnight = eventStart.getHours() === 0 && eventStart.getMinutes() === 0;
    const endsAtEndOfDay = eventEnd.getHours() === 23 && eventEnd.getMinutes() === 59;
    const spansSingleDay = isSameDay(eventStart, eventEnd);

    return isSameDay(eventStart, date) && startsAtMidnight && endsAtEndOfDay && spansSingleDay;
  };

  // 🌟 PHASE 1: Date Generation for Month View
  const daysInMonthView = React.useMemo(() => {
    if (view !== 'month') return [];

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentDate, view]);

  /**
   * 📐 GRID MATH - Adjusted for Day/Week views with all-day row
   */
  const getEventStyle = (event: EngineEvent, columnEvents?: EngineEvent[]) => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);

    const base = {
      top: `${getEventPosition(startDate, compactHourHeight, allDayRowHeight)}px`,
      height: `${getEventHeight(startDate, endDate, compactHourHeight)}px`,
    };

    if (!columnEvents) return base;

    // Side-by-side layout for events that overlap in time within this column — each event in a
    // cluster gets width = 1/cluster.length, offset by its index, with a small fixed gutter so
    // adjacent events don't visually touch.
    const clusters = clusterOverlappingEvents(columnEvents);
    const cluster = clusters.find((c) => c.some((e) => e.id === event.id));
    if (!cluster || cluster.length <= 1) return base;

    const indexInCluster = cluster.findIndex((e) => e.id === event.id);
    const widthPct = 100 / cluster.length;
    const gutterPx = 2;

    return {
      ...base,
      left: `calc(${indexInCluster * widthPct}% + ${indexInCluster === 0 ? 6 : gutterPx / 2}px)`,
      width: `calc(${widthPct}% - ${indexInCluster === cluster.length - 1 ? 6 : gutterPx}px)`,
      right: 'auto' as const,
    };
  };

  // 👥 PERSPECTIVE LOGIC: Adjusted for Personal vs Shared Mode
  const isMultiMember = currentHouseholdId && activeMembers.length > 1 && view === 'day';
  // 🌟 PHASE 2: Adjust columnCount for Month View
  const columnCount = view === 'month' ? 7 : (isMultiMember ? activeMembers.length : (view === 'week' ? 7 : 1));

  const dayLabels = [
    t('calendar.day_label.sun', 'Sun'),
    t('calendar.day_label.mon', 'Mon'),
    t('calendar.day_label.tue', 'Tue'),
    t('calendar.day_label.wed', 'Wed'),
    t('calendar.day_label.thu', 'Thu'),
    t('calendar.day_label.fri', 'Fri'),
    t('calendar.day_label.sat', 'Sat')
  ];

  useEffect(() => {
    if (!calendarGridBodyRef.current) return;
    if (view === 'day' || view === 'week') {
      const now = new Date();
      const currentHour = now.getHours() + now.getMinutes() / 60;
      const scrollTo = allDayRowHeight + currentHour * compactHourHeight - 200;
      calendarGridBodyRef.current.scrollTop = Math.max(0, scrollTo);
    } else {
      calendarGridBodyRef.current.scrollTop = 0;
    }
  }, [view, currentDate, allDayRowHeight, compactHourHeight]);

  const handleMouseDownOnGridColumn = useCallback((e: React.MouseEvent<HTMLDivElement>, colIndex: number) => {
    // Only allow left-click to start drag
    if (e.button !== 0) return;

    // Prevent drag if clicking on an existing event or resize handle (to be implemented)
    const target = e.target as HTMLElement;
    if (target.closest('.event-item') || target.closest('.event-resize-handle')) {
        return;
    }

    if (calendarGridBodyRef.current) {
      const gridRect = calendarGridBodyRef.current.getBoundingClientRect();
      const startY = e.clientY - gridRect.top;
      const snappedY = snapToInterval(startY - allDayRowHeight, compactHourHeight);

      setIsDraggingForCreate(true);
      setDragStartPixelY(snappedY);
      setDragCurrentPixelY(snappedY);
      setDraggedColumnIndex(colIndex);

      e.preventDefault();
    }
  }, [allDayRowHeight, compactHourHeight]);


  const handleMouseMoveOnGridBody = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingForCreate || !calendarGridBodyRef.current) return;

    const gridRect = calendarGridBodyRef.current.getBoundingClientRect();
    const currentY = e.clientY - gridRect.top;
    const snappedY = snapToInterval(currentY - allDayRowHeight, compactHourHeight);

    setDragCurrentPixelY(snappedY);
  }, [isDraggingForCreate, allDayRowHeight, compactHourHeight]);

  const handleMouseUpOnGridBody = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingForCreate || draggedColumnIndex === -1) return;

    setIsDraggingForCreate(false);

    const startY = Math.min(dragStartPixelY, dragCurrentPixelY);
    let endY = Math.max(dragStartPixelY, dragCurrentPixelY);

    const minHeightPixels = snapToInterval(15, compactHourHeight, 15);
    if (endY - startY < minHeightPixels) {
      endY = startY + minHeightPixels;
    }

    // Determine the target date for the column
    let targetDateForColumn: Date;
    if (view === 'day') {
      targetDateForColumn = currentDate;
    } else if (view === 'week') {
      targetDateForColumn = addDays(startOfWeek(currentDate), draggedColumnIndex);
    } else {
        // Should not happen for month view which doesn't have drag-to-create currently
        console.warn("Drag-to-create not supported for month view.");
        return;
    }

    // Convert pixels to Date objects
    const newEventStart = pixelsToDateTime(startY, targetDateForColumn, compactHourHeight, 15);
    const newEventEnd = pixelsToDateTime(endY, targetDateForColumn, compactHourHeight, 15);

    // Validate if end is after start, if not, swap or ensure a minimum duration
    if (isBefore(newEventEnd, newEventStart)) {
      const temp = newEventStart;
      // Ensure a minimum 15-minute duration if swapped
      onDragToCreate(newEventEnd, new Date(temp.getTime() + 15 * 60 * 1000), draggedColumnIndex);
    } else {
      onDragToCreate(newEventStart, newEventEnd, draggedColumnIndex);
    }

    // Reset drag state
    setDragStartPixelY(0);
    setDragCurrentPixelY(0);
    setDraggedColumnIndex(-1);
  }, [isDraggingForCreate, dragStartPixelY, dragCurrentPixelY, draggedColumnIndex, view, currentDate, onDragToCreate, compactHourHeight, allDayRowHeight]);

  // Attach/detach mousemove and mouseup listeners to the document for robust dragging
  useEffect(() => {
    if (isDraggingForCreate) {
      document.addEventListener('mousemove', handleMouseMoveOnGridBody as any);
      document.addEventListener('mouseup', handleMouseUpOnGridBody as any);
    } else {
      document.removeEventListener('mousemove', handleMouseMoveOnGridBody as any);
      document.removeEventListener('mouseup', handleMouseUpOnGridBody as any);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveOnGridBody as any);
      document.removeEventListener('mouseup', handleMouseUpOnGridBody as any);
    };
  }, [isDraggingForCreate, handleMouseMoveOnGridBody, handleMouseUpOnGridBody]);

return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-slate-950 overflow-hidden select-none">

      {/* 🗓️ GRID HEADER: Hybrid Scope Indicators */}
      {/* The ml-16 is applied here to push the header content over, aligning with the actual grid content */}
      <div className={clsx(
        "flex border-b border-slate-100 dark:border-slate-400 bg-white dark:bg-slate-950 z-20 sticky top-0",
        view === 'month' ? '' : 'ml-16'
      )}>
        <div
          className="flex-1 grid"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(${isMultiMember ? '300px' : '0'}, 1fr))`,
            width: isMultiMember || view === 'month' ? 'max-content' : '100%'
          }}
        >
          {view === 'month' ? (
            dayLabels.map((day: string) => (
              <div key={day} className="flex-1 py-4 text-center border-l border-slate-50 dark:border-slate-900">
                <span className="text-2xs font-black uppercase tracking-widest text-slate-400">{day}</span>
              </div>
            ))
          ) : isMultiMember ? (
            activeMembers.map((mId: string) => {
              const member = householdMembers.find((h: HouseholdMember) => h.userId === mId);
              const displayDate = currentDate; // In multi-member day view, the date is constant
              const isToday = isSameDay(displayDate, new Date());

              return (
                <div key={mId} className="min-w-[300px] py-4 text-center border-l border-slate-50 dark:border-slate-900 flex flex-col items-center justify-center gap-1">
                  <span className={clsx(
                    "text-[10px] font-black uppercase tracking-widest",
                    isToday ? "text-brand" : "text-slate-400"
                  )}>
                    {format(displayDate, 'eee d')}
                  </span>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center text-[10px] font-black text-brand uppercase">
                      {member?.name?.[0] || t('calendar.unknown_user_initial', 'U')}
                    </div>
                    <span className="text-2xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-200">
                      {member?.name || t('calendar.user', 'User')}
                    </span>
                  </div>
                </div>
              );
            })
          ) : view === 'week' ? (
            Array.from({ length: 7 }).map((_, i) => {
              const day = addDays(startOfWeek(currentDate), i);
              const isToday = isSameDay(day, new Date());
              return (
                <div key={i} className="flex-1 py-4 text-center border-l border-slate-50 dark:border-slate-900">
                  <span className={clsx(
                    "text-2xs font-black uppercase tracking-widest",
                    isToday ? "text-brand" : "text-slate-400"
                  )}>
                    {format(day, 'eee d')}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="flex-1 py-4 px-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={clsx(
                  "text-2xs font-black uppercase tracking-widest",
                  isSameDay(currentDate, new Date()) ? "text-brand" : "text-slate-400 italic"
                )}>
                  {format(currentDate, 'EEEE, MMMM d')}
                </span>
                <span className="text-2xs font-black uppercase tracking-widest text-brand italic">
                  {currentHouseholdId ? t('calendar.household_agenda', "Household Agenda") : t('calendar.personal_agenda', "Personal Agenda")}
                </span>
              </div>
              {/* 🛡️ PRIVACY SHIELD: Visual confirmation of Data Scoping */}
              <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 dark:bg-slate-900 rounded-full border border-slate-100 dark:border-slate-800">
                {activeScope === 'PERSONAL' || !currentHouseholdId ? <UserIcon className="w-3 h-3 text-emerald-500" /> : <Layers className="w-3 h-3 text-brand" />}
                <span className="text-[8px] font-black uppercase tracking-tighter text-slate-400">
                  {currentHouseholdId ? activeScope : t('calendar.personal_mode_active', 'Personal Mode Active')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🌊 SCROLLABLE BODY */}
      <div
        ref={calendarGridBodyRef} // 🌟 Attach ref here
        className="flex-1 overflow-auto no-scrollbar relative calendar-grid-container"
        onMouseMove={handleMouseMoveOnGridBody} // 🌟 Listen for mouse move on the entire body
        onMouseUp={handleMouseUpOnGridBody}   // 🌟 Listen for mouse up on the entire body
      >
        {view === 'month' ? ( // Month View Rendering
          <div className="h-full w-full">
            <div
              className="grid grid-cols-7 h-full w-full divide-x divide-y border-t border-l border-slate-50 dark:divide-slate-700 dark:border-slate-700"
              style={{ gridTemplateRows: `repeat(${Math.ceil(daysInMonthView.length / 7)}, minmax(80px, 1fr))` }}
            >
              {daysInMonthView.map((day: Date, index: number) => {
                const isToday = isSameDay(day, new Date());
                const isOutsideMonth = !isSameMonth(day, currentDate);
                const eventsForDay = events.filter(event => isSameDay(new Date(event.start), day));

                return (
                  <div
                    key={index}
                    className={clsx(
                      "relative p-2 h-full border-b border-r border-slate-50 dark:border-slate-900/30 flex flex-col items-center cursor-pointer",
                      isOutsideMonth && "opacity-40 text-slate-400 dark:text-slate-600",
                      isToday && "bg-brand/5 dark:bg-brand/10"
                    )}
                    onClick={() => {
                      onDateChange(day);
                      onViewChange('day');
                    }}
                  >
                    <span className={clsx(
                      "text-2xs font-black uppercase w-full text-center mb-1",
                      isToday && "text-brand"
                    )}>
                      {format(day, 'd')}
                    </span>
                    <div className="flex flex-col items-center gap-1 mt-2 overflow-y-auto no-scrollbar h-[calc(100%-24px)]">
                      {eventsForDay.slice(0, 3).map((event: EngineEvent) => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          className="w-full px-2 py-1 rounded-md text-[10px] font-bold text-white truncate cursor-pointer"
                          style={{ backgroundColor: event.color || '#5e88fc' }}
                          title={event.title}
                        >
                          {format(new Date(event.start), 'HH:mm')} - {event.title}
                        </div>
                      ))}
                      {eventsForDay.length > 3 && (
                        <div className="w-full px-2 py-1 rounded-md text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-700 mt-1">
                          {t('calendar.more_events', `+${eventsForDay.length - 3} more`)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : ( // Existing Day/Week View rendering
          <div
            className="flex min-h-max relative"
            style={{
              height: `${allDayRowHeight + (24 * compactHourHeight)}px`,
              width: isMultiMember ? 'max-content' : '100%'
            }}
          >
            <div className="absolute top-0 left-16 right-0 z-30 flex" style={{ height: `${allDayRowHeight}px` }}>
              {Array.from({ length: columnCount }).map((_, colIdx: number) => {
                let targetDate: Date;
                let columnMemberId: string | undefined;

                if (view === 'day') {
                  targetDate = currentDate;
                  columnMemberId = isMultiMember ? activeMembers[colIdx] : undefined;
                } else { // week view
                  targetDate = addDays(startOfWeek(currentDate), colIdx);
                  // In week view, there's no member column, so columnMemberId is not relevant here
                }

                const allDayEventsForColumn = events.filter(event => {
                  const eventStartDate = new Date(event.start);
                  
                  // Must be an all-day event for the specific target date
                  if (!isAllDayEventForDate(event, targetDate)) return false;

                  // If multi-member, ensure event owner matches the column's member
                  if (isMultiMember && columnMemberId && event.ownerId !== columnMemberId) return false;

                  return true;
                });

                return (
                  <div
                    key={colIdx}
                    className="relative h-full w-full border-b border-r border-slate-100 dark:border-slate-400 p-1 flex flex-col gap-0.5"
                    style={{ minWidth: isMultiMember ? '300px' : '0' }}
                  >
                    {allDayEventsForColumn.map(event => (
                      <div
                        key={event.id}
                        onClick={() => onEventClick(event)}
                          className="w-full px-2 py-1 rounded-md text-[10px] font-bold text-white truncate cursor-pointer"
                          style={{ backgroundColor: event.color || '#5e88fc' }}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="w-16 flex-none border-r border-slate-100 dark:border-slate-600 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm sticky left-0 z-30">
              {/* All-Day label in the time column */}
              <div style={{ height: `${allDayRowHeight}px` }} className="border-b border-slate-100 dark:border-slate-600 flex items-end justify-end pb-1 pr-3">
                 <span className="text-[9px] font-black text-slate-300 dark:text-slate-700 uppercase">
                  {t('calendar.all_day', 'All Day')}
                </span>
              </div>
              {hours.map((hour: number) => (
                <div key={hour} className="relative" style={{ height: `${compactHourHeight}px` }}>
                  <span className="absolute top-1 right-3 text-[9px] font-black text-slate-300 dark:text-slate-700 uppercase">
                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : hour === 0 ? '12 AM' : `${hour} AM`}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="flex-1 relative grid divide-x divide-slate-50 dark:divide-slate-700"
              style={{
                gridTemplateColumns: `repeat(${columnCount}, minmax(${isMultiMember ? '300px' : '0'}, 1fr))`
                ,
                width: isMultiMember ? `calc(${columnCount * 300}px)` : '100%',
              }}
            >
              <div className="absolute inset-0 pointer-events-none">
                {hours.map((hour: number) => (
                  <div
                    key={hour}
                    className="w-full border-b border-slate-50 dark:border-slate-700"
                    style={{ height: `${compactHourHeight}px` }}
                  />
                ))}
              </div>

              {(view === 'day' || view === 'week') && <TimeIndicator hourHeight={compactHourHeight} topOffset={allDayRowHeight} />}

              {Array.from({ length: columnCount }).map((_, colIdx: number) => {
                let targetDate: Date;
                let columnMemberId: string | undefined;

                if (view === 'day') {
                  targetDate = currentDate;
                  columnMemberId = isMultiMember ? activeMembers[colIdx] : undefined;
                } else { // week view
                  targetDate = addDays(startOfWeek(currentDate), colIdx);
                  // In week view, there's no member column, so columnMemberId is not relevant here
                }

                return (
                  <div
                    key={colIdx}
                    className="relative h-full w-full"
                    onMouseDown={(e) => handleMouseDownOnGridColumn(e, colIdx)} // 🌟 Listen for mouse down on column for drag-to-create
                  >
                    {/* 🌟 Temporary Drag-to-Create Event Visual */}
                    {isDraggingForCreate && draggedColumnIndex === colIdx && (
                      <div
                        className="absolute left-1.5 right-1.5 bg-brand/20 border border-brand/60 rounded-lg pointer-events-none z-30 flex items-center justify-center text-brand text-2xs font-bold"
                        style={{
                          top: `${Math.min(dragStartPixelY, dragCurrentPixelY) + allDayRowHeight}px`,
                          height: `${Math.max(24, Math.abs(dragCurrentPixelY - dragStartPixelY))}px`,
                        }}
                      >
                        {t('calendar.new_event', 'New')}
                      </div>
                    )}
                    {(() => {
                      const columnEvents = events.filter((event: EngineEvent) => {
                        const eventStartDate = new Date(event.start);

                        // Event must be on the target date for this column
                        if (!isSameDay(eventStartDate, targetDate)) return false;

                        // Exclude all-day events here; they are rendered in the all-day row
                        if (isAllDayEventForDate(event, targetDate)) return false;

                        // If multi-member view (day view), only show events for the current column's member
                        if (isMultiMember && columnMemberId) {
                          return event.ownerId === columnMemberId;
                        }

                        // Otherwise (single-member day view or week view), show events for the target date
                        return true;
                      });

                      return columnEvents.map((event: EngineEvent) => (
                        <div
                          key={event.id}
                          onClick={() => onEventClick(event)}
                          className={`absolute left-1.5 right-1.5 group transition-all duration-300 ${event.isSuggestion ? 'opacity-50 z-10' : 'z-20'} event-item`}
                          style={getEventStyle(event, columnEvents)}
                        >
                          {isTravelEnabled && event.location?.address && (
                            <TravelBuffer event={event} hourHeight={compactHourHeight} />
                          )}
                          {isTravelEnabled && event.travelMetadata?.estimatedDuration && (
                            <div className="absolute -top-5 left-0 right-0 flex justify-center pointer-events-none">
                              {event.travelMetadata.isImpossible ? (
                                <span className="text-[8px] font-black uppercase tracking-tighter bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  ⚠ Impossible — insufficient travel time
                                </span>
                              ) : (
                                <span className="text-[8px] font-black uppercase tracking-tighter bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  {`🚗 Leave by ${new Date(new Date(event.start).getTime() - (event.travelMetadata.estimatedDuration * 60000) - (((event as any).arrivalBufferMins ?? 15) * 60000)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`}
                                </span>
                              )}
                            </div>
                          )}

                          <div                           className={clsx(
                            "h-full w-full p-4 rounded-[1.5rem] border shadow-sm flex flex-col justify-between overflow-hidden cursor-pointer hover:shadow-xl transition-all",
                            "bg-white dark:bg-slate-900" // Keep default background for the card
                          )}
                          style={{ borderColor: event.color || '#5e88fc' }}>
                            <div className="relative z-10">
                              <div className="flex items-center gap-2 mb-1">
                                {event.source === 'personal' && <UserIcon className="w-2 h-2" style={{ color: event.color || '#5e88fc' }} />}
                                <h4 className="text-[11px] font-black text-slate-900 dark:text-slate-100 leading-tight truncate uppercase italic tracking-tighter">
                                  {event.title}
                                </h4>
                              </div>
                              {event.location?.address && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  <MapPin className="w-2.5 h-2.5" style={{ color: event.color || '#5e88fc' }} />
                                  <p className="text-[8px] font-bold text-slate-400 truncate tracking-tight">
                                    {event.location.address}
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="flex justify-between items-end">
                              {event.isSuggestion && (
                                <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-100 dark:border-amber-900/30">
                                  <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                                  <span className="text-[7px] font-black uppercase text-amber-600">{t('calendar.proposal', 'Proposal')}</span>
                                </div>
                              )}
                              <div className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-auto tabular-nums">
                                {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};